import React from 'react';
import { Building2, UserCircle2 } from 'lucide-react';

const initialsOf = (name) =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * WhatsApp-style conversation list, shared across CRM / Employee / Customer.
 * Props:
 *   threads        — [{ thread_id, title, audience, last_message_preview, last_message_at, unread, child_name }]
 *   activeThreadId — currently open thread id
 *   onSelect(thread)
 *   emptyText
 */
export const ChatThreadList = ({ threads = [], activeThreadId, onSelect, emptyText = 'No conversations yet.' }) => {
  if (!threads.length) {
    return <p className="p-6 text-center text-sm text-[#6B6B7A]" data-testid="chat-threads-empty">{emptyText}</p>;
  }
  return (
    <div className="divide-y divide-[#F0EDE6]" data-testid="chat-thread-list">
      {threads.map((t) => {
        const active = t.thread_id === activeThreadId;
        const isBiz = t.audience === 'business';
        return (
          <button
            key={t.thread_id}
            type="button"
            onClick={() => onSelect?.(t)}
            className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors ${active ? 'bg-[#FF3B6B]/5' : 'hover:bg-[#FFFDF8]'}`}
            data-testid={`chat-thread-${t.thread_id}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isBiz ? 'bg-[#118AB2]/12 text-[#118AB2]' : 'bg-[#FF3B6B]/12 text-[#FF3B6B]'}`}>
              {isBiz ? <Building2 className="w-5 h-5" /> : (t.title ? <span className="text-xs font-bold">{initialsOf(t.title)}</span> : <UserCircle2 className="w-5 h-5" />)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-sm truncate ${t.unread > 0 ? 'font-bold text-[#1E1E24]' : 'font-semibold text-[#1E1E24]'}`}>{t.title || 'Conversation'}</p>
                <span className="text-[10px] text-[#9a9aa6] flex-shrink-0">{timeAgo(t.last_message_at)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-xs truncate ${t.unread > 0 ? 'text-[#1E1E24]' : 'text-[#6B6B7A]'}`}>
                  {t.audience === 'host' && t.child_name ? <span className="text-[#FF3B6B] font-medium">{t.child_name}&apos;s party · </span> : null}
                  {t.last_message_preview || 'No messages yet'}
                </p>
                {t.unread > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B6B] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0" data-testid={`chat-thread-unread-${t.thread_id}`}>
                    {t.unread > 9 ? '9+' : t.unread}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ChatThreadList;
